/**
 * BrazeIterableCustomerIoIntegrationsPage — 3-vendor marketing-event integration.
 * Users pick a vendor (Braze | Iterable | Customer.io), store a credential for that
 * vendor, forward user events to the vendor's API, and audit recently forwarded events.
 *
 * UI:
 *   - Vendor picker (3 tabs: Braze, Iterable, Customer.io)
 *   - Credential form per-vendor (api_key + workspace)
 *   - Existing configs for selected vendor (click to focus event tools)
 *   - Event-mapping form for the focused config (external_event_id -> event_name + user_email)
 *   - Recent-sync feed (last forwarded events for focused config)
 *
 * Colour rules: GREEN-only accent. No emerald/blue/orange/purple/dark/emoji.
 * Palette:  green-600/700 text, green-600 border/bg, zinc for neutrals.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useIntegrationConfigsList,
	useIntegrationConfigCreate,
	useIntegrationConfigDelete,
	useIntegrationEventsList,
	useIntegrationEventCreate,
} from "./hooks";
import type { Vendor } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const TAB_ON =
	"rounded-[6px] border border-green-700 bg-green-700 px-4 py-2 text-sm font-medium text-white";
const TAB_OFF =
	"rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:border-green-600 hover:text-green-700";

const VENDORS: { id: Vendor; label: string; hint: string }[] = [
	{ id: "braze", label: "Braze", hint: "REST API key + workspace" },
	{ id: "iterable", label: "Iterable", hint: "Server-side API key + project" },
	{ id: "customer_io", label: "Customer.io", hint: "Track API key + site ID" },
];

export function BrazeIterableCustomerIoIntegrationsPage() {
	const [vendor, setVendor] = useState<Vendor>("braze");
	const [focusedConfigId, setFocusedConfigId] = useState<string | null>(null);

	const [apiKey, setApiKey] = useState("");
	const [workspace, setWorkspace] = useState("");

	const [externalEventId, setExternalEventId] = useState("");
	const [eventName, setEventName] = useState("");
	const [userEmail, setUserEmail] = useState("");

	const configsQuery = useIntegrationConfigsList();
	const createConfig = useIntegrationConfigCreate();
	const deleteConfig = useIntegrationConfigDelete();
	const eventsQuery = useIntegrationEventsList(focusedConfigId);
	const createEvent = useIntegrationEventCreate(focusedConfigId);

	const allConfigs = configsQuery.data?.items ?? [];
	const vendorConfigs = useMemo(
		() => allConfigs.filter((c) => c.provider === vendor),
		[allConfigs, vendor],
	);

	const vendorMeta = VENDORS.find((v) => v.id === vendor);

	const handleCreateConfig = (e: FormEvent) => {
		e.preventDefault();
		if (!apiKey.trim() || !workspace.trim()) return;
		createConfig.mutate(
			{ provider: vendor, api_key: apiKey.trim(), workspace: workspace.trim() },
			{
				onSuccess: (row) => {
					setApiKey("");
					setWorkspace("");
					setFocusedConfigId(row.id);
				},
			},
		);
	};

	const handleSwitchVendor = (v: Vendor) => {
		setVendor(v);
		setFocusedConfigId(null);
		setApiKey("");
		setWorkspace("");
	};

	const handleCreateEvent = (e: FormEvent) => {
		e.preventDefault();
		if (!focusedConfigId) return;
		if (!externalEventId.trim() || !eventName.trim()) return;
		createEvent.mutate(
			{
				external_event_id: externalEventId.trim(),
				event_name: eventName.trim(),
				user_email: userEmail.trim() || undefined,
			},
			{
				onSuccess: () => {
					setExternalEventId("");
					setEventName("");
					setUserEmail("");
				},
			},
		);
	};

	const events = eventsQuery.data?.items ?? [];

	return (
		<main
			data-testid="braze-iterable-customer-io-integrations-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					braze-iterable-customer-io-integrations
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Marketing event integrations
				</h1>
				<p className="text-sm text-zinc-600">
					Forward user events from your product into Braze, Iterable, or
					Customer.io. Pick a vendor, store a credential, then map events.
				</p>
			</header>

			<section aria-label="Vendor picker">
				<div
					role="tablist"
					aria-label="Integration vendors"
					className="flex flex-wrap gap-2"
					data-testid="braze-iterable-customer-io-integrations-vendor-tabs"
				>
					{VENDORS.map((v) => {
						const on = vendor === v.id;
						return (
							<button
								key={v.id}
								type="button"
								role="tab"
								aria-selected={on}
								className={on ? TAB_ON : TAB_OFF}
								data-testid={`braze-iterable-customer-io-integrations-tab-${v.id}`}
								onClick={() => handleSwitchVendor(v.id)}
							>
								{v.label}
							</button>
						);
					})}
				</div>
			</section>

			<section
				className={CARD}
				aria-label={`Add ${vendorMeta?.label ?? ""} credential`}
			>
				<h2 className="text-base font-semibold text-zinc-900">
					{vendorMeta?.label} credential
				</h2>
				<p className="mt-1 text-xs text-zinc-600">{vendorMeta?.hint}</p>
				<form
					onSubmit={handleCreateConfig}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="braze-iterable-customer-io-integrations-create-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="API key"
						value={apiKey}
						onChange={(e) => setApiKey(e.currentTarget.value)}
						disabled={createConfig.isPending}
						data-testid="braze-iterable-customer-io-integrations-api-key-input"
					/>
					<input
						type="text"
						className={INPUT}
						placeholder="Workspace / project / site ID"
						value={workspace}
						onChange={(e) => setWorkspace(e.currentTarget.value)}
						disabled={createConfig.isPending}
						data-testid="braze-iterable-customer-io-integrations-workspace-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={
							createConfig.isPending || !apiKey.trim() || !workspace.trim()
						}
						data-testid="braze-iterable-customer-io-integrations-submit"
					>
						{createConfig.isPending ? "Saving" : "Save credential"}
					</button>
				</form>
				{createConfig.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createConfig.error.message}
					</p>
				) : null}
			</section>

			<section
				className={CARD}
				aria-label={`${vendorMeta?.label ?? ""} configs`}
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Connected {vendorMeta?.label} workspaces
				</h2>
				{configsQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-iterable-customer-io-integrations-loading"
					>
						Loading
					</p>
				) : configsQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{configsQuery.error.message}
					</p>
				) : vendorConfigs.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-iterable-customer-io-integrations-empty"
					>
						No {vendorMeta?.label} workspace connected yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="braze-iterable-customer-io-integrations-list"
					>
						{vendorConfigs.map((row) => {
							const focused = row.id === focusedConfigId;
							return (
								<li
									key={row.id}
									className="flex items-center justify-between gap-3 py-2 text-sm"
									data-testid={`braze-iterable-customer-io-integrations-row-${row.id}`}
								>
									<div className="flex flex-col">
										<span className="font-medium text-zinc-900">
											{row.workspace}
										</span>
										<span className="font-mono text-xs text-zinc-500">
											{row.id}
										</span>
									</div>
									<div className="flex items-center gap-2">
										<button
											type="button"
											className={focused ? TAB_ON : BTN_GHOST}
											onClick={() => setFocusedConfigId(row.id)}
											data-testid={`braze-iterable-customer-io-integrations-focus-${row.id}`}
										>
											{focused ? "Focused" : "Focus"}
										</button>
										<button
											type="button"
											className={BTN_GHOST}
											onClick={() => {
												if (focusedConfigId === row.id) setFocusedConfigId(null);
												deleteConfig.mutate(row.id);
											}}
											disabled={deleteConfig.isPending}
											data-testid={`braze-iterable-customer-io-integrations-delete-${row.id}`}
										>
											Disable
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</section>

			<section
				className={CARD}
				aria-label="Event mapping"
				data-testid="braze-iterable-customer-io-integrations-event-section"
			>
				<h2 className="text-base font-semibold text-zinc-900">Event mapping</h2>
				{!focusedConfigId ? (
					<p className="mt-2 text-sm text-zinc-500">
						Focus a workspace above to forward events to it.
					</p>
				) : (
					<>
						<p className="mt-1 text-xs text-zinc-600">
							Forwarding to config {" "}
							<span className="font-mono text-zinc-800">{focusedConfigId}</span>
							. Requests are idempotent by external event id.
						</p>
						<form
							onSubmit={handleCreateEvent}
							className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4"
							data-testid="braze-iterable-customer-io-integrations-event-form"
						>
							<input
								type="text"
								className={INPUT}
								placeholder="External event id"
								value={externalEventId}
								onChange={(e) => setExternalEventId(e.currentTarget.value)}
								disabled={createEvent.isPending}
								data-testid="braze-iterable-customer-io-integrations-event-id-input"
							/>
							<input
								type="text"
								className={INPUT}
								placeholder="Event name"
								value={eventName}
								onChange={(e) => setEventName(e.currentTarget.value)}
								disabled={createEvent.isPending}
								data-testid="braze-iterable-customer-io-integrations-event-name-input"
							/>
							<input
								type="email"
								className={INPUT}
								placeholder="User email (optional)"
								value={userEmail}
								onChange={(e) => setUserEmail(e.currentTarget.value)}
								disabled={createEvent.isPending}
								data-testid="braze-iterable-customer-io-integrations-event-email-input"
							/>
							<button
								type="submit"
								className={BTN}
								disabled={
									createEvent.isPending ||
									!externalEventId.trim() ||
									!eventName.trim()
								}
								data-testid="braze-iterable-customer-io-integrations-event-submit"
							>
								{createEvent.isPending ? "Forwarding" : "Forward event"}
							</button>
						</form>
						{createEvent.error ? (
							<p className="mt-2 text-xs text-red-600" role="alert">
								{createEvent.error.message}
							</p>
						) : null}
					</>
				)}
			</section>

			<section
				className={CARD}
				aria-label="Recent sync feed"
				data-testid="braze-iterable-customer-io-integrations-feed-section"
			>
				<h2 className="text-base font-semibold text-zinc-900">Recent syncs</h2>
				{!focusedConfigId ? (
					<p className="mt-2 text-sm text-zinc-500">
						Focus a workspace to view forwarded events.
					</p>
				) : eventsQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-iterable-customer-io-integrations-events-loading"
					>
						Loading
					</p>
				) : eventsQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{eventsQuery.error.message}
					</p>
				) : events.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="braze-iterable-customer-io-integrations-events-empty"
					>
						No events forwarded yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="braze-iterable-customer-io-integrations-events-list"
					>
						{events.map((ev) => (
							<li
								key={ev.id}
								className="flex items-center justify-between py-2 text-sm"
								data-testid={`braze-iterable-customer-io-integrations-event-row-${ev.id}`}
							>
								<div className="flex flex-col">
									<span className="font-medium text-zinc-900">
										{ev.event_name ?? "(no name)"}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										{ev.external_event_id ?? ev.id}
									</span>
								</div>
								<span className="text-xs text-zinc-600">{ev.status}</span>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default BrazeIterableCustomerIoIntegrationsPage;
